import type { ReactNode } from 'react';
import { useId } from 'react';
import { cn } from '../cn.js';
import { Label } from './label.js';

export interface FormFieldProps {
  label: string;
  /** Optional helper text shown below the control. */
  description?: string;
  /** Validation message — when set, the field renders in error state. */
  error?: string;
  required?: boolean;
  className?: string;
  /**
   * Render the control, receiving the field id and an `invalid` flag.
   *
   *   <FormField label="Email" error={emailError}>
   *     {({ id, invalid }) => <Input id={id} invalid={invalid} ... />}
   *   </FormField>
   */
  children: (args: { id: string; invalid: boolean; describedBy: string | undefined }) => ReactNode;
}

export function FormField({
  label,
  description,
  error,
  required,
  className,
  children,
}: FormFieldProps) {
  const id = useId();
  const descId = `${id}-desc`;
  const errId = `${id}-err`;
  const describedBy = error ? errId : description ? descId : undefined;
  const invalid = Boolean(error);

  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id}>
        {label}
        {required ? (
          <span aria-hidden className="ml-0.5 text-destructive">
            *
          </span>
        ) : null}
      </Label>
      {children({ id, invalid, describedBy })}
      {description && !error ? (
        <p id={descId} className="text-xs text-muted-foreground">
          {description}
        </p>
      ) : null}
      {error ? (
        <p id={errId} className="text-xs font-medium text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
